import {
  SPARKY_FRAMEWORK,
  ADMIN_CLUSTER_URL_NAME,
  PAAS_REDIRECT_URL_NAME,
  API_SERVICE_URLS_NAME,
  API_GATEWAY_URL_KEY,
  API_GW_GLOBAL_SVC_URL_KEY,
  APP_REGISTRY_URL_KEY,
  INSTANCE_STORE_URL_KEY,
  TENANCY_SERVICE_URL_KEY,
  SUBSCRIPTION_SERVICE_URL_KEY,
  IAM_SERVICE_URL_KEY,
  IDENTITY_SERVICE_URL_KEY,
  CDL_APP_ID,
  FAWKES_APP_ID,
  PA_PANORAMA_APP_ID,
  FAWKES_MICRO_APP_NAME,
  SDWAN_MICRO_APP_NAME,
  SDWAN_APP_ID,
  SDWAN_PATH_PREFIX,
  COSMOS_URL_KEY,
  PREFERENCE_SERVICE_URL_KEY,
  SCM_CLIENT_IDS,
  PAI_URL_KEY,
  INSIGHTS_URL_KEY,
  PROP_INSIGHTS_FQDN,
  SPIFFY_URL_KEY,
  MSP_BULK_CONFIG_APP_ID,
  PROP_SPARKY_SERVICE_URLS,
} from './constants'
import { stateRef } from './store'
import { createDeferredPromise, isObjectLike, isString } from './utils'
import type { SparkySettings } from './microAppTypes'
import type { DeferredPromise, Authed, MainState, FrameworkVars, ServiceOptions, AppsCanBeLaunched, AppCanBeLaunched, AuthState, AuthedCallback } from './stateTypes'
import type { Instance } from './indexedArrays'

export type InitedPromise = DeferredPromise<SparkySettings> & {
  settings: SparkySettings
  starts: number
}

export const inited = createDeferredPromise<SparkySettings>() as InitedPromise
export const authed = createDeferredPromise<Authed>()
export const loaded = createDeferredPromise<MainState>()
export const authenticated = createDeferredPromise<AuthState>()
export const authorized = createDeferredPromise<AuthState>()

Object.defineProperty(inited, 'starts', { value: Date.now() })

export const getInitSettings = (): SparkySettings => inited.settings

const getFrameworkVarFn: (<T = any>(name?: string) => T) | (() => FrameworkVars) = (name?: string) => {
  const fxVars = inited.settings?.variables?.[SPARKY_FRAMEWORK] as FrameworkVars
  return fxVars && name ? fxVars[name] : (fxVars || {})
}
export const getFrameworkVar = getFrameworkVarFn as <T = any>(name?: string) => T | undefined
export const getFrameworkVars = getFrameworkVarFn as () => FrameworkVars
export const getOidcClientId = (): string | undefined => {
  const { oidc_client_id, oidc_client } = getFrameworkVars()
  return oidc_client_id || oidc_client?.client_id || undefined
}
export const isOidcClient = (clientId: string | undefined): boolean => {
  if (!clientId) {
    return false
  }
  const currentClientId = getOidcClientId()!
  if (currentClientId === clientId) {
    return true
  }
  if (SCM_CLIENT_IDS.includes(clientId)) {
    return SCM_CLIENT_IDS.includes(currentClientId)
  }
  return false
  // return Boolean(clientId) && getOidcClientId() === clientId
}

/**
 * Check if the micro-app exists
 * @param name - micro-app name
 * @returns exists or not as boolean
 */
export const hasMicroApp = (name: string): boolean => {
  return Boolean(name && inited.settings?.microAppNameSet?.has(name))
}

const APPS_DEFAULT_LAUNCH_CFG = {
  [FAWKES_APP_ID]: { app_id: FAWKES_APP_ID }, // auto
  [SDWAN_APP_ID]: { app_id: SDWAN_APP_ID, default_route: SDWAN_PATH_PREFIX },
} as AppsCanBeLaunched
const appsCanBeLaunched = {} as AppsCanBeLaunched
export const getAppCanBeLaunched = (appId: string | null | undefined): AppCanBeLaunched | undefined => {
  if (!appId || getFrameworkVar('hub')) {
    return
  }
  if (!getOidcClientId()) { // non-tsg for fawkes only
    return APPS_DEFAULT_LAUNCH_CFG[FAWKES_APP_ID]
  }
  if (appsCanBeLaunched[FAWKES_APP_ID]) { // cached
    return appsCanBeLaunched[appId]
  }
  const appsVar = getFrameworkVar<Record<string, AppCanBeLaunched | null>>('apps_can_launch')
  if (!appsVar || !Object.keys(appsVar).length) { // auto default
    if (getFrameworkVar('fawkes_session') || hasMicroApp(FAWKES_MICRO_APP_NAME)) {
      appsCanBeLaunched[FAWKES_APP_ID] = APPS_DEFAULT_LAUNCH_CFG[FAWKES_APP_ID]
    }
    if (hasMicroApp(SDWAN_MICRO_APP_NAME)) {
      appsCanBeLaunched[SDWAN_APP_ID] = APPS_DEFAULT_LAUNCH_CFG[SDWAN_APP_ID]
    }
  }
  else {
    for (const appId of Object.keys(appsVar)) {
      const app = appsVar[appId]
      if (app) {
        appsCanBeLaunched[appId] = app
      }
    }
    if (appsCanBeLaunched[FAWKES_APP_ID]) { // use prisma_access config for pa_panorama
      appsCanBeLaunched[PA_PANORAMA_APP_ID] = appsCanBeLaunched[FAWKES_APP_ID]
    }
  }
  return appsCanBeLaunched[appId]
}

export const isAppCanBeLaunched = (appIdOrInstance: Instance | string | null | undefined): boolean => {
  if (getFrameworkVar('hub')) {
    return false
  }
  const appId = (appIdOrInstance as Instance)?.app_id || appIdOrInstance as string | null | undefined
  if (!appId) {
    return false
  }
  if (!getOidcClientId()) { // non-tsg for fawkes only
    return appId === FAWKES_APP_ID
  }
  const appCanLaunchCfg = getAppCanBeLaunched(appId)
  if (!appCanLaunchCfg) {
    return false
  }
  if (typeof appIdOrInstance === 'string') {
    return true
  }
  const { url } = appIdOrInstance as Instance
  if (appCanLaunchCfg.origin) {
    let originCfg = appCanLaunchCfg.origin.toLowerCase() as string
    // if (originCfg === 'location-origin') {
    //   originCfg = window.location.origin
    // }
    originCfg = originCfg.startsWith('https://') ? originCfg : `https://${originCfg}`
    const origin = url?.startsWith('http') && new URL(url).origin.toLowerCase()
    if (origin && originCfg !== origin) {
      return false
    }
  }
  return true
}

export const getCurrentPlatformRegion = (preferredAppId?: string): string | undefined => {
  const tsgRegion = stateRef.current.auth?.instances?.getPlatformRegion?.(preferredAppId)
  if (tsgRegion) {
    return tsgRegion
  }
  // for non-tsg env
  const tenantInfo = stateRef.current.main?.tenant?.info
  if (preferredAppId?.startsWith(FAWKES_APP_ID)) {
    return tenantInfo?.region
  }
  const tenantRegion = tenantInfo?.associations?.find((asso: { app_id: string }) => asso.app_id === CDL_APP_ID)?.region || tenantInfo?.region
  return tenantRegion
}
const API_GW_PATH_MAP = {
  [INSTANCE_STORE_URL_KEY]: '/subscription/v1',
  [SUBSCRIPTION_SERVICE_URL_KEY]: '/subscription/v1',
  [TENANCY_SERVICE_URL_KEY]: '/tenancy/v1',
  [IAM_SERVICE_URL_KEY]: '/iam/v1',
  [IDENTITY_SERVICE_URL_KEY]: '/identity/v1',
  [PREFERENCE_SERVICE_URL_KEY]: '/preference/api/v1',
  [APP_REGISTRY_URL_KEY]: '/app-registry',
} as Record<string, string | undefined>
export const getRawServiceUrlMappingOrString = (name: string): string | Record<string, string> | undefined => {
  const urlVar = getFrameworkVar(API_SERVICE_URLS_NAME)?.[name] // as override
  if (isString(urlVar)) {
    return urlVar
  }
  const urlProp = stateRef.current.auth?.properties?.none?.get(PROP_SPARKY_SERVICE_URLS)?.[name] // as default
  if (!urlProp) {
    return urlVar && { ...urlVar }
  }
  if (isString(urlProp)) {
    return urlProp
  }
  return { ...urlProp, ...urlVar }
}
const getRegionalMappingUrl = (name: string, options?: ServiceOptions): string | undefined => {
  const urlMapping = (getRawServiceUrlMappingOrString(name) || {}) as Record<string, string>
  const mappingKeyValue = options?.mappingKey && urlMapping[options.mappingKey]
  if (mappingKeyValue) {
    return mappingKeyValue
  }
  const region = options?.region || getCurrentPlatformRegion()
  return (region && urlMapping[region]) || urlMapping.default || undefined
}
export const ensureHttps = (fqdn?: string) => (!fqdn || fqdn.startsWith('https:') ? fqdn : `https://${fqdn}`)
export const ensureHttpsOrigin = (fqdn?: string) => {
  const origin = ensureHttps(fqdn)
  if (!origin) {
    return origin
  }
  try {
    const url = new URL(origin)
    return url.origin
  }
  catch (e) {
    console.error('bad url', origin, e)
    return origin?.endsWith('/') ? origin.slice(0, -1) : origin
  }
}
export const getFrameworkServiceUrl = (name: string, options?: ServiceOptions): string | undefined => {
  if ([COSMOS_URL_KEY, PAI_URL_KEY, INSIGHTS_URL_KEY].includes(name)) {
    if (!options?.region && !options?.mappingKey) {
      const mfeProp = stateRef.current.auth?.properties?.get(PROP_INSIGHTS_FQDN)
      const mfePropUrl = mfeProp && ensureHttpsOrigin(mfeProp.url || mfeProp.fqdn) as string | undefined
      if (mfePropUrl) {
        return mfePropUrl
      }
      const mainFqdn = stateRef.current.main?.fqdnInfo?.msg?.paifqdn
      if (mainFqdn) {
        return ensureHttpsOrigin(mainFqdn)
      }
    }
    return ensureHttpsOrigin(getRegionalMappingUrl(name, options) || window.__insights_api_server)
  }
  if (name === SPIFFY_URL_KEY && !options?.region && !options?.mappingKey) {
    const urlFromRuntimeAttr = stateRef.current.auth?.instances?.get(FAWKES_APP_ID)?.runtime_attributes?.spiffy_api_url
    if (urlFromRuntimeAttr) {
      return ensureHttpsOrigin(urlFromRuntimeAttr)
    } // otherwise fallback to use regional mapping
  }
  if (name === ADMIN_CLUSTER_URL_NAME) {
    const { auth, main, fxVars } = stateRef.current
    const adminUrl = auth?.fawkes_admin_api_url ||
      main?.tenant?.info?.runtime_attributes?.admin_api_url ||
      auth?.instances?.get(FAWKES_APP_ID)?.runtime_attributes?.admin_api_url ||
      (fxVars.msp && auth?.instances?.get(MSP_BULK_CONFIG_APP_ID)?.runtime_attributes?.admin_api_url) ||
      getFrameworkVar(name) || getRawServiceUrlMappingOrString('default_admin_cluster_url') || window.__admin_cluster_url
    return ensureHttpsOrigin(adminUrl)
  }
  if (name === PAAS_REDIRECT_URL_NAME) {
    const { auth, main, fxVars } = stateRef.current
    const redirectUrl = main?.tenant?.cluster?.redirectURL ||
      auth?.fawkes_paas_api_url ||
      main?.tenant?.info?.runtime_attributes?.paas_api_url ||
      auth?.instances?.get(FAWKES_APP_ID)?.runtime_attributes?.paas_api_url ||
      (fxVars.msp && auth?.instances?.get(MSP_BULK_CONFIG_APP_ID)?.runtime_attributes?.paas_api_url) || undefined
    const paasRedirectUrl = ensureHttpsOrigin(redirectUrl) // could return undefined
    return paasRedirectUrl ? `${paasRedirectUrl}/` : paasRedirectUrl // ensure ending / due to tickets
  }
  if (name === API_GW_GLOBAL_SVC_URL_KEY) {
    return (getRawServiceUrlMappingOrString(API_GW_GLOBAL_SVC_URL_KEY) ||
      getRawServiceUrlMappingOrString(API_GATEWAY_URL_KEY)) as string | undefined
  }
  const apiGwPath = getFrameworkVar('use_api_gateway') !== false ? API_GW_PATH_MAP[name] : undefined
  if (apiGwPath) {
    const apiPath = API_GW_PATH_MAP[name]
    if (apiPath?.startsWith('/')) {
      const apiGwUrl = getRawServiceUrlMappingOrString(API_GW_GLOBAL_SVC_URL_KEY) as string ||
        getRawServiceUrlMappingOrString(API_GATEWAY_URL_KEY) as string
      if (!apiGwUrl) {
        return undefined
      }
      const url = new URL(apiPath, apiGwUrl).toString()
      API_GW_PATH_MAP[name] = url
      return url
    }
    if (apiPath) {
      return apiPath
    }
  }
  const targetUrl = getRawServiceUrlMappingOrString(name)
  if (targetUrl && isObjectLike(targetUrl)) {
    return getRegionalMappingUrl(name, options)
  }
  return targetUrl
}

/**
 * @deprecated use tsgLoad callback for nav v2, and state loader extension for v3 instead
 */
export const onAuthed = (cb: AuthedCallback) => {
  // cb no return
  authed.then(cb).catch((err) => {
    console.error('prepare load info error', err)
  })
}

export { stateRef }
export { history } from './history'
export * from './store'
export { events } from './events'
